/*
 * Shows a temporary visual cue only when the associated sidebar
 * scroller contains content below its visible area.
 */
var HeaderSidebarScrollCue = class extends HTMLElement {
  constructor() {
    super();

    this.scroller = null;
    this.sidebar = null;
    this.sidebarIsOpen = false;
    this.dismissed = false;
    this.updateFrame = null;
    this.updateTimer = null;
    this.resizeObserver = null;
    this.mutationObserver = null;

    this.handleScroll = this.handleScroll.bind(this);
    this.handleAfterShow = this.handleAfterShow.bind(this);
    this.handleBeforeHide = this.handleBeforeHide.bind(this);
    this.scheduleUpdate = this.scheduleUpdate.bind(this);
  }

  connectedCallback() {
    this.scroller = this.previousElementSibling?.matches(
      ".header-sidebar__scroller"
    )
      ? this.previousElementSibling
      : null;

    this.sidebar = this.closest("header-sidebar");

    if (!this.scroller || !this.sidebar) {
      return;
    }

    this.sidebarIsOpen = this.sidebar.hasAttribute("open");

    this.scroller.addEventListener("scroll", this.handleScroll, {
      passive: true
    });

    this.sidebar.addEventListener(
      "dialog:after-show",
      this.handleAfterShow
    );

    this.sidebar.addEventListener(
      "dialog:before-hide",
      this.handleBeforeHide
    );

    window.addEventListener("resize", this.scheduleUpdate, {
      passive: true
    });

    window.visualViewport?.addEventListener(
      "resize",
      this.scheduleUpdate,
      { passive: true }
    );

    /*
     * Recheck when viewport size, menu height, or accordion content
     * changes without relying on a breakpoint.
     */
    if ("ResizeObserver" in window) {
      this.resizeObserver = new ResizeObserver(this.scheduleUpdate);
      this.resizeObserver.observe(this.scroller);
      this.observeScrollerChildren();
    }

    this.mutationObserver = new MutationObserver((records) => {
      const activePanelChanged = records.some(
        (record) => record.attributeName === "aria-activedescendant"
      );

      if (activePanelChanged) {
        this.dismissed = false;
      }

      if (records.some((record) => record.type === "childList")) {
        this.observeScrollerChildren();
      }

      this.scheduleUpdate();

      /*
       * Run again after Prestige finishes a nested-panel or
       * accordion animation.
       */
      window.clearTimeout(this.updateTimer);
      this.updateTimer = window.setTimeout(this.scheduleUpdate, 350);
    });

    this.mutationObserver.observe(this.scroller, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["hidden", "open"]
    });

    const collapsiblePanel = this.closest(
      "header-sidebar-collapsible-panel"
    );

    if (collapsiblePanel) {
      this.mutationObserver.observe(collapsiblePanel, {
        attributes: true,
        attributeFilter: ["aria-activedescendant"]
      });
    }

    document.fonts?.ready.then(() => {
      if (this.isConnected) {
        this.scheduleUpdate();
      }
    });

    this.scheduleUpdate();
  }

  disconnectedCallback() {
    this.scroller?.removeEventListener("scroll", this.handleScroll);

    this.sidebar?.removeEventListener(
      "dialog:after-show",
      this.handleAfterShow
    );

    this.sidebar?.removeEventListener(
      "dialog:before-hide",
      this.handleBeforeHide
    );

    window.removeEventListener("resize", this.scheduleUpdate);

    window.visualViewport?.removeEventListener(
      "resize",
      this.scheduleUpdate
    );

    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();

    window.clearTimeout(this.updateTimer);

    if (this.updateFrame !== null) {
      cancelAnimationFrame(this.updateFrame);
    }
  }

  handleAfterShow() {
    this.sidebarIsOpen = true;
    this.dismissed = false;
    this.scheduleUpdate();
  }

  handleBeforeHide() {
    this.sidebarIsOpen = false;
    this.hideCue();
  }

  handleScroll() {
    if (this.scroller.scrollTop > 4) {
      this.dismissed = true;
      this.hideCue();
      return;
    }

    this.scheduleUpdate();
  }

  observeScrollerChildren() {
    if (!this.resizeObserver) {
      return;
    }

    Array.from(this.scroller.children).forEach((child) => {
      this.resizeObserver.observe(child);
    });
  }

  scheduleUpdate() {
    if (this.updateFrame !== null) {
      cancelAnimationFrame(this.updateFrame);
    }

    this.updateFrame = requestAnimationFrame(() => {
      this.updateFrame = null;
      this.updateCue();
    });
  }

  updateCue() {
    const overflowAmount =
      this.scroller.scrollHeight - this.scroller.clientHeight;

    const shouldShow =
      this.sidebarIsOpen &&
      !this.dismissed &&
      this.scroller.scrollTop <= 4 &&
      overflowAmount > 12;

    if (!shouldShow) {
      this.hideCue();
      return;
    }

    const scrollerRect = this.scroller.getBoundingClientRect();
    const offsetParent = this.offsetParent;

    if (
      !offsetParent ||
      scrollerRect.width <= 0 ||
      scrollerRect.height <= 0
    ) {
      this.hideCue();
      return;
    }

    /*
     * Position the cue against the scroller's visible bottom edge.
     * This automatically accounts for the optional account/localization
     * footer beneath the main menu.
     */
    const offsetParentRect = offsetParent.getBoundingClientRect();

    const left =
      scrollerRect.left -
      offsetParentRect.left +
      offsetParent.scrollLeft -
      offsetParent.clientLeft;

    const top =
      scrollerRect.bottom -
      offsetParentRect.top +
      offsetParent.scrollTop -
      offsetParent.clientTop -
      this.offsetHeight;

    this.style.left = left + "px";
    this.style.top = top + "px";
    this.style.width = scrollerRect.width + "px";

    this.classList.add("is-visible");
  }

  hideCue() {
    this.classList.remove("is-visible");
  }
};

if (!window.customElements.get("header-sidebar-scroll-cue")) {
  window.customElements.define(
    "header-sidebar-scroll-cue",
    HeaderSidebarScrollCue
  );
}